"use strict";
/**
 * Example Flag Definitions and Programs
 *
 * This module demonstrates the proper separation between:
 * - CODE LAYER (FlagDefinition): Defines flag existence and value types
 * - DATA LAYER (FlagProgram, SegmentDefinition): Defines evaluation logic with explicit inputs
 *
 * Examples show:
 * - Simple boolean flags
 * - Flags with segment dependencies (DAG chain: segment -> flag)
 * - Flags with flag dependencies (DAG chain: flag -> flag)
 * - Explicit input dependencies for the watch set
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXAMPLE_FLAG_CONFIGS = exports.EXAMPLE_FLAG_PROGRAMS = exports.PROGRAM_FEATURE_TIER = exports.PROGRAM_MAX_UPLOAD_SIZE = exports.PROGRAM_ADVANCED_BETA_FEATURES = exports.PROGRAM_BETA_FEATURES_ENABLED = exports.PROGRAM_NEW_FEATURE_ENABLED = exports.EXAMPLE_SEGMENTS = exports.SEGMENT_EDUCATIONAL_USERS = exports.SEGMENT_BETA_TESTERS = exports.SEGMENT_INTERNAL_USERS = exports.EXAMPLE_FLAG_REGISTRY = exports.FLAG_FEATURE_TIER = exports.FLAG_MAX_UPLOAD_SIZE = exports.FLAG_ADVANCED_BETA_FEATURES = exports.FLAG_BETA_FEATURES_ENABLED = exports.FLAG_NEW_FEATURE_ENABLED = void 0;
const definitions_1 = require("./definitions");
const rules_1 = require("./rules");
// =============================================================================
// CODE LAYER: Flag Definitions (type/schema only, NO evaluation logic)
// =============================================================================
/**
 * Example flag: New Feature Enabled
 * A simple boolean flag for enabling a new feature
 */
exports.FLAG_NEW_FEATURE_ENABLED = (0, definitions_1.defineBooleanFlag)("new-feature-enabled", {
    description: "Enable the new experimental feature",
    owner: "platform-team",
    tags: ["experiment", "ui"],
});
/**
 * Example flag: Beta Features Enabled
 * A boolean flag that will be targeted to beta testers via FlagProgram
 */
exports.FLAG_BETA_FEATURES_ENABLED = (0, definitions_1.defineBooleanFlag)("beta-features-enabled", {
    description: "Enable beta features for beta testers",
    owner: "platform-team",
    tags: ["beta", "ui"],
});
/**
 * Example flag: Advanced Beta Features
 * A boolean flag that depends on beta-features-enabled (DAG chain)
 */
exports.FLAG_ADVANCED_BETA_FEATURES = (0, definitions_1.defineBooleanFlag)("advanced-beta-features", {
    description: "Enable advanced beta features (requires beta features to be enabled)",
    owner: "platform-team",
    tags: ["beta", "advanced"],
});
/**
 * Example flag: Max Upload Size
 * A number flag with min/max constraints
 */
exports.FLAG_MAX_UPLOAD_SIZE = (0, definitions_1.defineNumberFlag)("max-upload-size", {
    description: "Maximum file upload size in MB",
    owner: "platform-team",
    tags: ["limits"],
    min: 1,
    max: 1000,
});
/**
 * Example flag: Feature Tier
 * A string flag with allowed values (enum-like)
 */
exports.FLAG_FEATURE_TIER = (0, definitions_1.defineStringFlag)("feature-tier", {
    description: "The feature tier for the user",
    owner: "platform-team",
    tags: ["tier"],
    allowedValues: ["free", "pro", "enterprise"],
});
/**
 * Example flag registry containing all example flags (CODE layer)
 */
exports.EXAMPLE_FLAG_REGISTRY = {
    "new-feature-enabled": exports.FLAG_NEW_FEATURE_ENABLED,
    "beta-features-enabled": exports.FLAG_BETA_FEATURES_ENABLED,
    "advanced-beta-features": exports.FLAG_ADVANCED_BETA_FEATURES,
    "max-upload-size": exports.FLAG_MAX_UPLOAD_SIZE,
    "feature-tier": exports.FLAG_FEATURE_TIER,
};
// =============================================================================
// DATA LAYER: Segment Definitions (with explicit input dependencies)
// =============================================================================
/**
 * Example segment: Internal Users
 * Users with @flux.ai email addresses
 *
 * Note: inputs.userAttributes explicitly declares what this segment reads
 */
exports.SEGMENT_INTERNAL_USERS = {
    id: "internal-users",
    name: "Internal Users",
    description: "Users with @flux.ai email addresses",
    condition: (0, rules_1.emailEndsWith)("@flux.ai"),
    inputs: {
        userAttributes: ["user.email"],
        systemAttributes: [],
        segments: [],
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example segment: Beta Testers
 * Users who are either internal users OR have the beta tester attribute
 *
 * Note: This segment depends on another segment (internal-users) AND a user attribute
 */
exports.SEGMENT_BETA_TESTERS = {
    id: "beta-testers",
    name: "Beta Testers",
    description: "Users who are internal users or have opted into beta testing",
    condition: (0, rules_1.or)((0, rules_1.inSegment)("internal-users"), (0, rules_1.attributeEquals)("user.customAttributes.isBetaTester", true)),
    inputs: {
        userAttributes: ["user.customAttributes.isBetaTester"],
        systemAttributes: [],
        segments: ["internal-users"], // Explicit segment dependency
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example segment: Educational Users
 * Users with educational qualification
 */
exports.SEGMENT_EDUCATIONAL_USERS = {
    id: "educational-users",
    name: "Educational Users",
    description: "Users with educational qualification",
    condition: (0, rules_1.attributeEquals)("user.isEducational", true),
    inputs: {
        userAttributes: ["user.isEducational"],
        systemAttributes: [],
        segments: [],
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example segments map (DATA layer)
 */
exports.EXAMPLE_SEGMENTS = {
    "internal-users": exports.SEGMENT_INTERNAL_USERS,
    "beta-testers": exports.SEGMENT_BETA_TESTERS,
    "educational-users": exports.SEGMENT_EDUCATIONAL_USERS,
};
// =============================================================================
// DATA LAYER: Flag Programs (complete evaluation logic with explicit inputs)
// =============================================================================
/**
 * Example flag program for new-feature-enabled
 * Simple flag with no targeting rules, just a default value
 */
exports.PROGRAM_NEW_FEATURE_ENABLED = {
    flagKey: "new-feature-enabled",
    enabled: true,
    defaultValue: false,
    rules: [],
    inputs: {
        userAttributes: [],
        systemAttributes: [],
        segments: [],
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example flag program for beta-features-enabled
 * Shows targeting based on segment membership
 *
 * DAG chain: segment (beta-testers) -> flag (beta-features-enabled)
 */
exports.PROGRAM_BETA_FEATURES_ENABLED = {
    flagKey: "beta-features-enabled",
    enabled: true,
    defaultValue: false,
    rules: [
        {
            id: "rule-beta-testers",
            description: "Enable for beta testers",
            condition: (0, rules_1.inSegment)("beta-testers"),
            value: true,
            enabled: true,
        },
    ],
    inputs: {
        userAttributes: [], // No direct user attributes, but segment has them
        systemAttributes: [],
        segments: ["beta-testers"], // Explicit segment dependency
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example flag program for advanced-beta-features
 * Shows targeting based on another flag's value (DAG dependency)
 *
 * DAG chain: segment (internal-users) -> flag (beta-features-enabled) -> flag (advanced-beta-features)
 */
exports.PROGRAM_ADVANCED_BETA_FEATURES = {
    flagKey: "advanced-beta-features",
    enabled: true,
    defaultValue: false,
    rules: [
        {
            id: "rule-requires-beta",
            description: "Only enable if beta features are enabled AND user is internal",
            condition: (0, rules_1.and)((0, rules_1.inSegment)("internal-users"), (0, rules_1.flagEquals)("beta-features-enabled", true)),
            value: true,
            enabled: true,
        },
    ],
    inputs: {
        userAttributes: [],
        systemAttributes: [],
        segments: ["internal-users"], // Explicit segment dependency
        flags: ["beta-features-enabled"], // Explicit flag dependency (DAG)
    },
    updatedAt: Date.now(),
};
/**
 * Example flag program for max-upload-size
 * Shows different values for different segments
 */
exports.PROGRAM_MAX_UPLOAD_SIZE = {
    flagKey: "max-upload-size",
    enabled: true,
    defaultValue: 25,
    rules: [
        {
            id: "rule-internal-users",
            description: "Higher limit for internal users",
            condition: (0, rules_1.inSegment)("internal-users"),
            value: 500,
            enabled: true,
        },
        {
            id: "rule-educational-users",
            description: "Higher limit for educational users",
            condition: (0, rules_1.inSegment)("educational-users"),
            value: 100,
            enabled: true,
        },
    ],
    inputs: {
        userAttributes: [],
        systemAttributes: [],
        segments: ["internal-users", "educational-users"], // Explicit segment dependencies
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example flag program for feature-tier
 * Shows string enum flag with segment-based targeting
 */
exports.PROGRAM_FEATURE_TIER = {
    flagKey: "feature-tier",
    enabled: true,
    defaultValue: "free",
    rules: [
        {
            id: "rule-internal-enterprise",
            description: "Internal users get enterprise tier",
            condition: (0, rules_1.inSegment)("internal-users"),
            value: "enterprise",
            enabled: true,
        },
        {
            id: "rule-educational-pro",
            description: "Educational users get pro tier",
            condition: (0, rules_1.inSegment)("educational-users"),
            value: "pro",
            enabled: true,
        },
    ],
    inputs: {
        userAttributes: [],
        systemAttributes: [],
        segments: ["internal-users", "educational-users"],
        flags: [],
    },
    updatedAt: Date.now(),
};
/**
 * Example flag programs map (DATA layer)
 */
exports.EXAMPLE_FLAG_PROGRAMS = {
    "new-feature-enabled": exports.PROGRAM_NEW_FEATURE_ENABLED,
    "beta-features-enabled": exports.PROGRAM_BETA_FEATURES_ENABLED,
    "advanced-beta-features": exports.PROGRAM_ADVANCED_BETA_FEATURES,
    "max-upload-size": exports.PROGRAM_MAX_UPLOAD_SIZE,
    "feature-tier": exports.PROGRAM_FEATURE_TIER,
};
/**
 * @deprecated Use EXAMPLE_FLAG_PROGRAMS instead
 */
exports.EXAMPLE_FLAG_CONFIGS = exports.EXAMPLE_FLAG_PROGRAMS;
//# sourceMappingURL=examples.js.map